import { useEffect } from "react";

import { BreakoutControlRoom } from "./breakout_control_room";
import { BreakoutRoom } from "./breakout_room";
import { CaptionsOutputErrorBoundary, CenteredErrorBoundary } from "./error_boundary";
import { TalkTimeLegendModal } from "./local_modal/talk_time/talk_time_legend_modal";
import { ClassTimerWidget, MobileTimerControls } from "./sidebar/timer/timer_controls";

import { BreakoutRoomsOverview } from "components/breakout_control_room/breakout_rooms_overview";
import { CaptionsOutput } from "components/captioning/captions_output";
import { ParticipantBar } from "components/participant_bar";
import { Stage } from "components/stage";
import { FeaturedMessageModal } from "components/stage/featured_message_modal";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useHasFocus } from "hooks/use_has_focus";
import { useIsMobile } from "hooks/use_is_mobile";
import { useSidebarAdjustment } from "hooks/use_sidebar_adjustment";
import { useUserProperties } from "hooks/use_user_properties";
import { setEnableStudentDrawing, setUserHasClassroomInFocus } from "shared/reducers/classroom";
import { CapabilityType, KeyboardShortcutSignalType } from "shared/types";
import { ClassEventTypeType } from "shared/types/class_event";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { RoleType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { createClassEvent, createMonitoringEvent } from "store/reducers/monitoring";
import { selectShowLiveCaptions } from "store/selectors/classroom";

function BreakoutContent({
  breakoutSessionId,
  isPresentationView,
}: {
  breakoutSessionId: string;
  isPresentationView: boolean;
}) {
  const canParticipateInClass = useCapability(CapabilityType.CAN_PARTICIPATE_IN_CLASS);
  const visitingBreakoutRoom = useAppSelector(
    classroomSelectors.selectVisitingBreakoutRoomForCurrentUser(),
  );

  return isPresentationView ? (
    <CenteredErrorBoundary text="The breakout rooms overview cannot be displayed">
      <BreakoutRoomsOverview breakoutSessionId={breakoutSessionId} />
    </CenteredErrorBoundary>
  ) : canParticipateInClass || !!visitingBreakoutRoom ? (
    <CenteredErrorBoundary text="Something went wrong with the breakout room." includeTechSupport>
      <BreakoutRoom breakoutSessionId={breakoutSessionId} />
    </CenteredErrorBoundary>
  ) : (
    <CenteredErrorBoundary text="The breakout control room cannot be displayed.">
      <BreakoutControlRoom breakoutSessionId={breakoutSessionId} />
    </CenteredErrorBoundary>
  );
}

function ClassroomContent() {
  const dispatch = useAppDispatch();
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const user = useUserProperties(currentUserId, ["enable_student_drawing", "role"]);
  const userHasLiveCaptionsEnabled = useAppSelector(selectShowLiveCaptions());
  const activeRoomId = useAppSelector(classroomSelectors.selectActiveRoomIdForCurrentUser());
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const isAvEnabled = useAppSelector(classroomSelectors.selectCurrentUserIsAvEnabled());
  const isMobile = useIsMobile();
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  const { panes, featured_in_modal } = useAppSelector(classroomSelectors.selectStage());
  const hasClassroomInFocus = useHasFocus();
  const widthAdjustment = useSidebarAdjustment();

  const keyboardShortcutSignal = useAppSelector(localSelectors.selectKeyboardShortcutSignal());

  const timerWidth = 296; // The timer is 148 x 38px but the timer is displayed at 2x size
  const timerHeight = 76;

  useEffect(() => {
    dispatch(
      createClassEvent({
        type: ClassEventTypeType.CLASSROOM_VISIBILITY,
        userId: currentUserId,
        roomId: activeRoomId,
        data: { isVisible: hasClassroomInFocus },
      }),
    );
  }, [dispatch, currentUserId, hasClassroomInFocus, activeRoomId]);

  // Initialize teacher's student drawing preference
  useEffect(() => {
    if (!user) return;
    if (user.role === RoleType.Teacher) {
      dispatch(setEnableStudentDrawing({ isEnabled: user.enable_student_drawing }));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (activeBreakoutSessionId) return;
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.SHOW_MAIN_CLASSROOM,
        userId: currentUserId,
        contextData: { isPresentationView, isMobile },
      }),
    );
  }, [dispatch, currentUserId, isMobile, isPresentationView, activeBreakoutSessionId]);

  useEffect(() => {
    dispatch(
      setUserHasClassroomInFocus({
        hasClassroomInFocus,
        userId: currentUserId,
      }),
    );
  }, [dispatch, hasClassroomInFocus, currentUserId]);

  if (!user) {
    return null;
  }

  return (
    <>
      {activeBreakoutSessionId ? (
        <CenteredErrorBoundary text="Something went wrong with the breakout." includeTechSupport>
          <BreakoutContent
            breakoutSessionId={activeBreakoutSessionId}
            isPresentationView={isPresentationView}
          />
        </CenteredErrorBoundary>
      ) : (
        <>
          {isAvEnabled && <ParticipantBar />}
          {canControlClassroom && isMobile && <MobileTimerControls />}
          {classTimer.open && isPresentationView && (
            <ClassTimerWidget
              doubleSize
              canControlClassroom={false}
              initialPosition={{
                left: window.innerWidth - timerWidth - 20,
                top: window.innerHeight - timerHeight - 20,
              }}
            />
          )}
          <div
            id="stage-container"
            className="flex flex-auto relative"
            style={{ width: widthAdjustment }}
          >
            <CenteredErrorBoundary text="The stage cannot be displayed">
              <Stage canControlClassroom={canControlClassroom} panes={panes} />
            </CenteredErrorBoundary>
            {keyboardShortcutSignal === KeyboardShortcutSignalType.ToggleTalkTimeOverlay && (
              <TalkTimeLegendModal />
            )}
            {featured_in_modal && <FeaturedMessageModal />}
          </div>
        </>
      )}
      <CaptionsOutputErrorBoundary
        text="The captions cannot be displayed"
        userHasLiveCaptionsEnabled={userHasLiveCaptionsEnabled}
      >
        <CaptionsOutput />
      </CaptionsOutputErrorBoundary>
    </>
  );
}

export { BreakoutContent, ClassroomContent };
